
import React, { useState } from "react";
import {
    Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, TextField, Box, Typography,
    Card, CardContent, Avatar, Chip, Stack, Tooltip,
    InputAdornment, Fade, Badge, IconButton, Zoom
} from '@mui/material';
import {
    PersonAdd, Search, Groups, CheckCircle,
    PersonAddAlt1, Schedule, Close
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ShiftAddButton = ({ groupCode, onAddEmployees, machineCode, supervisorId, onTriggerSnackbar }) => {
    const [employeesDialogOpen, setEmployeesDialogOpen] = useState(false);
    const [fetchedEmployees, setFetchedEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredEmployees = fetchedEmployees.filter(employee =>
        employee.empName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.empId.toString().includes(searchTerm)
    );

    const handleEmployeesDialogClose = () => {
        if (!submitting) {
            setEmployeesDialogOpen(false);
            setSearchTerm('');
            setSelectedEmployees([]);
        }
    };

    const handleAddClick = async () => {
        setLoading(true);

        try {
            const response = await axios.get(`${API_BASE_URL}/employees`, {
                params: { groupCode }
            });

            if (response.data.length > 0) {
                const normalizedEmployees = response.data.map(employee => ({
                    empId: employee.EmpID,
                    empName: employee.Name
                }));
                setFetchedEmployees(normalizedEmployees);
                setEmployeesDialogOpen(true);
            } else {
                onTriggerSnackbar("No employees found for the given group code.", "info");
            }
        } catch (error) {
            onTriggerSnackbar("Error fetching employees. Please try again.", "error");
            console.error("Error fetching employees:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCheckboxChange = (employee) => {
        setSelectedEmployees((prevSelected) => {
            if (prevSelected.some(e => e.empId === employee.empId)) {
                return prevSelected.filter(e => e.empId !== employee.empId);
            } else {
                return [...prevSelected, employee];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedEmployees.length === filteredEmployees.length) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees([...filteredEmployees]);
        }
    };

    const handleSubmitSelectedEmployees = async () => {
        setSubmitting(true);

        try {
            const loggedInDateTime = new Date().toISOString();
            const activeStatus = "Logged-IN";
            const payload = {
                supervisorId,
                loggedInDateTime,
                activeStatus,
                machineCode,
                employees: selectedEmployees.map(employee => ({
                    empId: employee.empId,
                    empName: employee.empName,
                }))
            };

            const res = await axios.post(`${API_BASE_URL}/manhourslogShift`, payload);

            if (res.status === 200) {
                onTriggerSnackbar("Shift logged successfully!", "success");
                onAddEmployees(selectedEmployees);
                setEmployeesDialogOpen(false);
                setSelectedEmployees([]);
                setSearchTerm('');
            } else {
                onTriggerSnackbar("Error logging shift. Please try again.", "error");
            }
        } catch (error) {
            onTriggerSnackbar("Error logging shift. Please check your connection.", "error");
            console.error("Error logging shift:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const isAllSelected = filteredEmployees.length > 0 && selectedEmployees.length === filteredEmployees.length;

    return (
        <Box>
            <Tooltip title="Add Employees to Shift" arrow>
                <IconButton
                    onClick={handleAddClick}
                    disabled={loading}
                    sx={{
                        width: 52,
                        height: 52,
                        borderRadius: '16px',
                        background: loading
                            ? 'rgba(142, 142, 147, 0.3)'
                            : 'linear-gradient(135deg, #30d158 0%, #28cd41 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: loading
                            ? 'none'
                            : '0 8px 25px rgba(48, 209, 88, 0.35)',
                        color: 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
                            borderRadius: '16px',
                            pointerEvents: 'none',
                            opacity: loading ? 0 : 1,
                        },


                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                >
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        ) : (
                            <PersonAdd sx={{ fontSize: 24 }} />
                        )}
                    </Box>
                </IconButton>
            </Tooltip>

            <Dialog
                open={employeesDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        handleEmployeesDialogClose();
                    }
                }}
                maxWidth="md"
                fullWidth
                TransitionComponent={Zoom}
                transitionDuration={400}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.25)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.4)',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                        overflow: 'hidden',
                        position: 'relative',
                        minHeight: '70vh',
                        maxHeight: '90vh',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
                            pointerEvents: 'none',
                            zIndex: 1,
                        },
                        '& > *': {
                            position: 'relative',
                            zIndex: 2,
                        }
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                    }
                }}
            >
                <DialogTitle sx={{ p: 4, pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            background: 'rgba(22, 163, 74, 0.25)',
                            backdropFilter: 'blur(20px)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid rgba(34, 197, 94, 0.4)',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.1) 100%)',
                                borderRadius: '16px',
                                pointerEvents: 'none',
                            }
                        }}>
                            <Groups sx={{ fontSize: 28, color: '#16a34a', zIndex: 1 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{
                                fontWeight: 700,
                                color: '#1d1d1f',
                                fontSize: '28px',
                                letterSpacing: '-0.5px',
                                lineHeight: 1.2,
                                mb: 0.5,
                            }}>
                                Add to Shift
                            </Typography>
                            <Typography variant="body1" sx={{
                                color: '#8e8e93',
                                fontSize: '16px',
                                fontWeight: 500,
                            }}>
                                Group {groupCode} • Select employees for the shift
                            </Typography>
                        </Box>
                        <Chip
                            icon={<Schedule sx={{ fontSize: 18 }} />}
                            label={`${fetchedEmployees.length} Available`}
                            sx={{
                                height: '36px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                border: '1px solid rgba(34, 197, 94, 0.4)',
                                fontWeight: 600,
                                fontSize: '14px',
                                boxShadow: '0 4px 12px rgba(21, 128, 61, 0.3)',
                                '& .MuiChip-icon': { color: 'white' }
                            }}
                        />
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ px: 4, py: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Fade in timeout={500}>
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {fetchedEmployees.length > 0 ? (
                                <Stack spacing={3} sx={{ height: '100%' }}>
                                    <Box>
                                        <Box sx={{
                                            display: 'flex',
                                            gap: 2,
                                            mb: 2,
                                            alignItems: 'stretch'
                                        }}>
                                            <TextField
                                                placeholder="Search employees by name or ID..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                variant="outlined"
                                                sx={{
                                                    flex: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '16px',
                                                        background: 'rgba(255, 255, 255, 0.8)',
                                                        backdropFilter: 'blur(25px)',
                                                        border: '1px solid rgba(255, 255, 255, 0.4)',
                                                        fontSize: '15px',
                                                        height: '100%',
                                                        '& fieldset': { border: 'none' },

                                                        '&.Mui-focused': {
                                                            background: 'rgba(255, 255, 255, 0.95)',

                                                            boxShadow: '0 8px 25px rgba(0, 122, 255, 0.15)',
                                                            border: '1px solid rgba(0, 122, 255, 0.3)',
                                                        },
                                                        transition: 'all 0.3s ease',
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Search sx={{ color: '#8e8e93', fontSize: 20 }} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: searchTerm && (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setSearchTerm('')}
                                                                sx={{ color: '#8e8e93' }}
                                                            >
                                                                <Close sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />

                                            <Card sx={{
                                                borderRadius: '16px',
                                                background: 'rgba(0, 122, 255, 0.08)',
                                                backdropFilter: 'blur(25px)',
                                                border: '1px solid rgba(0, 122, 255, 0.2)',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                minWidth: '380px',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
                                                    borderRadius: '16px',
                                                    pointerEvents: 'none',
                                                }
                                            }}>
                                                <CardContent sx={{ py: 1.5, px: 2, position: 'relative', zIndex: 1 }}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Box sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: '10px',
                                                                background: 'rgba(0, 122, 255, 0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}>
                                                                <PersonAddAlt1 sx={{ fontSize: 18, color: '#3b82f6' }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="subtitle1" sx={{
                                                                    color: '#1d1d1f',
                                                                    fontWeight: 600,
                                                                    fontSize: '14px',
                                                                    lineHeight: 1.2,
                                                                }}>
                                                                    Selection Summary
                                                                </Typography>
                                                                <Typography variant="body2" sx={{
                                                                    color: '#8e8e93',
                                                                    fontSize: '12px',
                                                                    fontWeight: 500,
                                                                    lineHeight: 1.2,
                                                                }}>
                                                                    {selectedEmployees.length} of {filteredEmployees.length} selected
                                                                </Typography>
                                                            </Box>
                                                        </Box>

                                                        <Button
                                                            onClick={handleSelectAll}
                                                            size="small"
                                                            sx={{
                                                                borderRadius: '10px',
                                                                px: 2.5,
                                                                py: 0.75,
                                                                fontSize: '13px',
                                                                fontWeight: 600,
                                                                background: 'rgba(0, 122, 255, 0.15)',
                                                                backdropFilter: 'blur(10px)',
                                                                color: '#007aff',
                                                                border: '1px solid rgba(0, 122, 255, 0.3)',
                                                                whiteSpace: 'nowrap',

                                                                transition: 'all 0.2s ease',
                                                            }}
                                                        >
                                                            {isAllSelected ? 'Deselect All' : 'Select All'}
                                                        </Button>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Box>

                                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography variant="subtitle2" sx={{
                                            color: '#1d1d1f',
                                            fontWeight: 700,
                                            fontSize: '16px',
                                            mb: 2,
                                        }}>
                                            Available Employees: ({filteredEmployees.length})
                                        </Typography>

                                        {filteredEmployees.length === 0 ? (
                                            <Box sx={{
                                                p: 3,
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.12) 50%, rgba(252, 211, 77, 0.08) 100%)',
                                                backdropFilter: 'blur(20px)',
                                                border: '1px solid rgba(245, 158, 11, 0.15)',
                                                textAlign: 'center',
                                                boxShadow: '0 8px 32px rgba(245, 158, 11, 0.1)',
                                                position: 'relative',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.2) 100%)',
                                                    borderRadius: '16px',
                                                    pointerEvents: 'none',
                                                }
                                            }}>
                                                <Typography variant="body2" sx={{
                                                    color: '#1d1d1f',
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                }}>
                                                    No employees found matching "{searchTerm}"
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Card sx={{
                                                flex: 1,
                                                borderRadius: '16px',
                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 250, 252, 0.8) 50%, rgba(241, 245, 249, 0.7) 100%)',
                                                backdropFilter: 'blur(25px)',
                                                border: '1px solid rgba(255, 255, 255, 0.5)',
                                                overflow: 'hidden',
                                                position: 'relative',
                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.3) 100%)',
                                                    borderRadius: '16px',
                                                    pointerEvents: 'none',
                                                }
                                            }}>
                                                <Box sx={{
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    height: '100%',
                                                    overflowY: 'auto',
                                                    p: 1,
                                                    '&::-webkit-scrollbar': { width: 8 },
                                                    '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.1)' },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: 'rgba(0, 0, 0, 0.2)',
                                                        borderRadius: 4
                                                    },
                                                }}>
                                                    <Box sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(3, 1fr)',
                                                        gap: 1,
                                                    }}>
                                                        {filteredEmployees.map((employee) => {
                                                            const isSelected = selectedEmployees.some(e => e.empId === employee.empId);

                                                            return (
                                                                <Box
                                                                    key={employee.empId}
                                                                    onClick={() => handleCheckboxChange(employee)}
                                                                    sx={{
                                                                        borderRadius: '10px',
                                                                        p: 1.25,
                                                                        cursor: 'pointer',
                                                                        background: isSelected
                                                                            ? 'linear-gradient(135deg, rgba(21, 128, 61, 0.2) 0%, rgba(22, 163, 74, 0.15) 100%)'
                                                                            : 'transparent',
                                                                        backdropFilter: isSelected ? 'blur(10px)' : 'none',
                                                                        border: isSelected
                                                                            ? '1px solid rgba(34, 197, 94, 0.4)'
                                                                            : '1px solid transparent',
                                                                        '&:hover': {
                                                                            background: isSelected
                                                                                ? 'linear-gradient(135deg, rgba(21, 128, 61, 0.3) 0%, rgba(22, 163, 74, 0.25) 100%)'
                                                                                : 'rgba(255, 255, 255, 0.3)',

                                                                        },
                                                                        transition: 'all 0.2s ease',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1,
                                                                    }}
                                                                >
                                                                    <Badge
                                                                        overlap="circular"
                                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                                        badgeContent={
                                                                            isSelected ? (
                                                                                <CheckCircle
                                                                                    sx={{
                                                                                        color: '#16a34a',
                                                                                        fontSize: 12,
                                                                                        bgcolor: 'white',
                                                                                        borderRadius: '50%'
                                                                                    }}
                                                                                />
                                                                            ) : null
                                                                        }
                                                                    >
                                                                        <Avatar
                                                                            sx={{
                                                                                bgcolor: isSelected ? '#16a34a' : 'rgba(142, 142, 147, 0.3)',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                fontSize: '12px',
                                                                                width: 36,
                                                                                height: 36,
                                                                            }}
                                                                        >
                                                                            {employee.empName.charAt(0)}
                                                                        </Avatar>
                                                                    </Badge>

                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="body1" sx={{
                                                                            fontWeight: isSelected ? 600 : 500,
                                                                            color: '#1d1d1f',
                                                                            fontSize: '13px',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            lineHeight: 1.3,
                                                                        }}>
                                                                            {employee.empName}
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{
                                                                            color: '#8e8e93',
                                                                            fontSize: '11px',
                                                                            fontWeight: 500,
                                                                            lineHeight: 1.2,
                                                                        }}>
                                                                            ID: {employee.empId}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box sx={{
                                                                        width: 18,
                                                                        height: 18,
                                                                        borderRadius: '50%',
                                                                        border: `2px solid ${isSelected ? '#16a34a' : 'rgba(142, 142, 147, 0.5)'}`,
                                                                        backgroundColor: isSelected ? '#16a34a' : 'transparent',
                                                                        display: 'flex', alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        transition: 'all 0.2s ease',
                                                                        flexShrink: 0,
                                                                    }}>
                                                                        {isSelected && (
                                                                            <Box sx={{
                                                                                width: 5,
                                                                                height: 5,
                                                                                borderRadius: '50%',
                                                                                backgroundColor: 'white',
                                                                            }} />
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            </Card>
                                        )}
                                    </Box>
                                </Stack>
                            ) : (
                                <Box sx={{
                                    p: 4,
                                    borderRadius: '16px',
                                    background: 'rgba(255, 193, 7, 0.08)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(255, 193, 7, 0.2)',
                                    textAlign: 'center',
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 100%)',
                                        borderRadius: '16px',
                                        pointerEvents: 'none',
                                    }
                                }}>
                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                        <Typography variant="body1" sx={{
                                            color: '#1d1d1f',
                                            fontWeight: 600,
                                            fontSize: '18px',
                                            mb: 1,
                                        }}>
                                            No employees found for Group {groupCode}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: '#8e8e93',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                        }}>
                                            Please check the group code or contact your administrator.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Fade>
                </DialogContent>

                <DialogActions sx={{ p: 4, pt: 2, gap: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Button
                        onClick={handleEmployeesDialogClose}
                        disabled={submitting}
                        sx={{
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            fontSize: '16px',
                            fontWeight: 600,
                            background: 'rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(20px)',
                            color: '#007aff',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.1) 100%)',
                                borderRadius: '12px',
                                pointerEvents: 'none',
                            },
                            '&:hover': {
                                background: 'rgba(255, 255, 255, 0.3)',

                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                            },
                            '&:disabled': { opacity: 0.6, transform: 'none' },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        Cancel
                    </Button>

                    <Box sx={{ flex: 1 }} />

                    {selectedEmployees.length > 0 && (
                        <Chip
                            label={`${selectedEmployees.length} selected`}
                            sx={{
                                height: '32px',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)',
                                backdropFilter: 'blur(10px)',
                                color: 'white',
                                border: '1px solid rgba(34, 197, 94, 0.4)',
                                fontWeight: 600,
                                fontSize: '13px',
                                mr: 1,
                                boxShadow: '0 2px 8px rgba(21, 128, 61, 0.25)',
                            }}
                        />
                    )}

                    <Button
                        variant="contained"
                        onClick={handleSubmitSelectedEmployees}
                        disabled={selectedEmployees.length === 0 || submitting}
                        startIcon={
                            submitting ? (
                                <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                            ) : (
                                <PersonAdd sx={{ fontSize: 20 }} />
                            )
                        }
                        sx={{
                            borderRadius: '12px',
                            px: 4,
                            py: 1.5,
                            fontSize: '16px',
                            fontWeight: 600,
                            minWidth: '180px',
                            background: selectedEmployees.length > 0 && !submitting
                                ? 'linear-gradient(135deg, #15803d 0%, #16a34a 100%)'
                                : 'rgba(142, 142, 147, 0.3)',
                            backdropFilter: 'blur(20px)',
                            color: 'white',
                            border: selectedEmployees.length > 0 && !submitting
                                ? '1px solid rgba(34, 197, 94, 0.4)'
                                : '1px solid rgba(142, 142, 147, 0.3)',
                            position: 'relative',
                            boxShadow: selectedEmployees.length > 0 && !submitting
                                ? '0 4px 12px rgba(21, 128, 61, 0.3)'
                                : 'none',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: selectedEmployees.length > 0 && !submitting
                                    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%)'
                                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                borderRadius: '12px',
                                pointerEvents: 'none',
                            },

                            '&:disabled': {
                                background: 'rgba(142, 142, 147, 0.3)',
                                color: 'rgba(255, 255, 255, 0.6)',
                                transform: 'none',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {submitting
                            ? "Adding..."
                            : `Add ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShiftAddButton;